import { doc, getDocFromServer } from 'firebase/firestore';
import { firestorePaths } from '@qkindex/contracts';
import { db } from '../../firebase';
import { useFeedback } from '../feedback/FeedbackProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { rapidCaptureDb } from './rapidCaptureDb';
import {
  processRapidCaptureRecord,
  rapidCaptureAuthority,
  applyCaptureStatus,
} from './rapidCaptureOutbox';
import { captureService } from '../../services/captureService';
import { subscribeToCaptureStatus } from '../../repositories/captureStatusSubscription';
import {
  RapidCaptureAuthority,
  RapidCaptureDraft,
  RapidCaptureRecord,
  RapidCaptureStats,
} from './rapidCaptureTypes';
import {
  discardRapidCaptureRecord,
  itemCreateMayHaveCommitted,
} from './rapidCaptureDiscard';
import { Item } from '../../domain/item';
import { shouldReconcileRapidCaptureRecord } from './rapidCaptureReconciliation';
import { getCollectionAiCapability } from '../../firebase/functions';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';
import { invalidateInventory } from '../collection/inventoryInvalidation';

// Role, policy and billing-period changes are time-based, so a cached "allowed" always
// expires. Servers that report headroom also bound it by remaining captures.
export const RAPID_CAPTURE_UPLOAD_CONCURRENCY = 3;
const CAPABILITY_CACHE_MS = 120_000;
const CAPABILITY_CACHE_LEGACY_MS = 30_000;
const CAPABILITY_LOW_WATER = 3;

type CapabilityCache = {
  epoch: string;
  costVersion: string;
  expiresAt: number;
  remaining: number;
};

const emptyStats: RapidCaptureStats = {
  pending: 0,
  active: 0,
  analysisProcessing: 0,
  confirming: 0,
  failed: 0,
  total: 0,
  bytes: 0,
};

export interface RapidCaptureCompletion {
  items: number;
  labels: number;
}

const newSessionBatch = (userId?: string) => ({
  userId,
  operationIds: new Set<string>(),
  itemCount: 0,
  labelCount: 0,
});

export function useRapidCaptureOutbox(
  authority?: RapidCaptureAuthority,
  authoritativeItems: Item[] = [],
  collectionScoped = false,
) {
  const { notify } = useFeedback();
  const userId = authority?.userId;
  const [records, setRecords] = useState<RapidCaptureRecord[]>([]);
  const [stats, setStats] = useState<RapidCaptureStats>(emptyStats);
  const [online, setOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine,
  );
  const [completion, setCompletion] = useState<RapidCaptureCompletion | null>(
    null,
  );

  const processing = useRef(false);
  const statusSubscriptions = useRef(
    new Map<
      string,
      {
        key: string;
        active: boolean;
        bootstrapped: boolean;
        stop: () => void;
      }
    >(),
  );
  const sessionBatch = useRef(newSessionBatch(userId));
  const authorityEpoch = authority
    ? `${authority.userId}:${authority.collectionId}:${authority.membershipVersion}:${authority.lifecycleVersion ?? 0}:${authority.compatibilityMode}`
    : undefined;
  const authorityCollectionId = authority?.collectionId;
  const authorityMembershipVersion = authority?.membershipVersion;
  const authorityLifecycleVersion = authority?.lifecycleVersion;
  const authorityCompatibilityMode = authority?.compatibilityMode;
  const activeAuthorityEpochRef = useRef(authorityEpoch);
  activeAuthorityEpochRef.current = authorityEpoch;
  // Short-lived: an allowed capability answer for this exact authority epoch.
  // The server still settles credits when the analysis is submitted.
  const capabilityCache = useRef<CapabilityCache | undefined>(undefined);
  const activeAuthorityRef = useRef(authority);
  activeAuthorityRef.current = authority;
  const durableStorageAvailable = typeof indexedDB !== 'undefined';

  const trackSessionRecords = useCallback(
    (nextRecords: RapidCaptureRecord[]) => {
      if (!userId) return;
      if (sessionBatch.current.userId !== userId) {
        sessionBatch.current = newSessionBatch(userId);
      }
      nextRecords
        .filter(({ state }) => state !== 'terminal')
        .forEach((record) => {
          if (sessionBatch.current.operationIds.has(record.operationId)) return;
          sessionBatch.current.operationIds.add(record.operationId);
          sessionBatch.current.itemCount += 1;
        });
    },
    [userId],
  );

  const finishSessionBatchIfComplete = useCallback(() => {
    const batch = sessionBatch.current;
    if (batch.userId !== userId || batch.operationIds.size) return;
    if (batch.itemCount > 0) {
      setCompletion({
        items: batch.itemCount,
        labels: batch.labelCount,
      });
    }
    sessionBatch.current = newSessionBatch(userId);
  }, [userId]);

  const refresh = useCallback(async () => {
    if (!userId || !durableStorageAvailable) {
      setRecords([]);
      setStats(emptyStats);
      return;
    }
    try {
      const [nextRecords, nextStats] = await Promise.all([
        rapidCaptureDb.list(userId),
        collectionScoped
          ? rapidCaptureDb.stats(userId, authorityCollectionId)
          : rapidCaptureDb.stats(userId),
      ]);
      const visibleRecords = collectionScoped
        ? nextRecords.filter(
            (record) => record.collectionId === authorityCollectionId,
          )
        : nextRecords;
      trackSessionRecords(visibleRecords);
      setRecords(visibleRecords);
      setStats(nextStats);
    } catch {
      setRecords([]);
      setStats(emptyStats);
    }
  }, [
    authorityCollectionId,
    collectionScoped,
    durableStorageAvailable,
    trackSessionRecords,
    userId,
  ]);

  const process = useCallback(async () => {
    if (!userId || !durableStorageAvailable || processing.current || !online)
      return;
    processing.current = true;
    const isActiveAuthority = () =>
      activeAuthorityEpochRef.current === authorityEpoch;
    try {
      const queued = await rapidCaptureDb.list(userId);
      // Authority maintenance stays sequential; only the per-record server
      // submissions overlap. Each record has its own operation identity.
      const ready: RapidCaptureRecord[] = [];
      for (const record of queued) {
        if (!isActiveAuthority()) break;
        if (
          record.collectionId !== authorityCollectionId ||
          record.compatibilityMode !== authorityCompatibilityMode
        )
          continue;
        if (record.membershipVersion !== authorityMembershipVersion) {
          await rapidCaptureDb.purgeCollectionAuthority(
            record.userId,
            record.collectionId,
            authorityMembershipVersion!,
            'membership-changed',
          );
          continue;
        }
        if (
          authorityLifecycleVersion !== undefined &&
          record.lifecycleVersion !== authorityLifecycleVersion
        ) {
          await rapidCaptureDb.terminalizeCollectionLifecycle(
            record.userId,
            record.collectionId,
            authorityLifecycleVersion,
          );
          continue;
        }
        if (['failed', 'confirming', 'terminal'].includes(record.state))
          continue;
        // Accepted captures receive pushed receipts independently of this batch.
        if (record.serverAccepted && !record.controlAction) continue;
        if (
          record.nextAttemptAt &&
          Date.parse(record.nextAttemptAt) > Date.now()
        )
          continue;
        ready.push(record);
      }
      let next = 0;
      const worker = async () => {
        while (isActiveAuthority()) {
          const record = ready[next];
          next += 1;
          if (!record) return;
          await processRapidCaptureRecord(record, isActiveAuthority, userId);
        }
      };
      // A failed worker must not release the lock while sibling uploads are active.
      await Promise.allSettled(
        Array.from(
          { length: Math.min(RAPID_CAPTURE_UPLOAD_CONCURRENCY, ready.length) },
          worker,
        ),
      );
    } catch {
      // Browsers without IndexedDB keep the normal Add Item workflow available.
    } finally {
      processing.current = false;
      if (isActiveAuthority()) await refresh();
    }
  }, [
    authorityCollectionId,
    authorityCompatibilityMode,
    authorityEpoch,
    authorityLifecycleVersion,
    authorityMembershipVersion,
    durableStorageAvailable,
    online,
    refresh,
    userId,
  ]);

  useEffect(() => {
    const subscriptions = statusSubscriptions.current;
    const wanted = new Map(
      records
        .filter(
          (record) =>
            online &&
            record.schemaVersion === 5 &&
            record.userId === userId &&
            record.collectionId === authorityCollectionId &&
            record.membershipVersion === authorityMembershipVersion &&
            record.lifecycleVersion === authority?.lifecycleVersion &&
            record.serverAccepted &&
            !record.controlAction &&
            !['confirming', 'terminal'].includes(record.state),
        )
        .map((record) => [record.operationId, record]),
    );
    subscriptions.forEach((entry, id) => {
      if (!wanted.has(id) || entry.key !== authorityEpoch) {
        entry.active = false;
        entry.stop();
        subscriptions.delete(id);
      }
    });
    wanted.forEach((record, id) => {
      if (subscriptions.has(id) || !authorityEpoch) return;
      const entry = {
        key: authorityEpoch,
        active: true,
        bootstrapped: false,
        stop: () => {},
      };
      subscriptions.set(id, entry);
      const current = () =>
        entry.active && activeAuthorityEpochRef.current === entry.key;
      const fail = async () => {
        if (!current()) return;
        entry.active = false;
        entry.stop();
        await rapidCaptureDb
          .update(record, (saved) =>
            saved.state === 'confirming'
              ? saved
              : {
                  ...saved,
                  state: 'failed',
                  lastError: {
                    category: 'network',
                    message:
                      'Live capture updates are unavailable. Retry to reconnect; the photo is retained.',
                  },
                },
          )
          .catch(() => undefined);
      };
      const attach = () => {
        if (!current()) return;
        entry.stop = subscribeToCaptureStatus(
          record,
          (receipt) => {
            if (current()) void applyCaptureStatus(record, receipt).catch(fail);
          },
          () => {
            if (!current()) return;
            entry.stop();
            // A single authorized read repairs pre-release captures whose status
            // projection does not exist. This is recovery, never periodic polling.
            if (entry.bootstrapped) {
              void fail();
              return;
            }
            entry.bootstrapped = true;
            void captureService
              .control(record, 'status')
              .then(async (receipt) => {
                if (!current()) return;
                await applyCaptureStatus(record, receipt);
                attach();
              })
              .catch(fail);
          },
        );
      };
      attach();
    });
  }, [
    records,
    online,
    userId,
    authorityEpoch,
    authorityCollectionId,
    authorityMembershipVersion,
    authority?.lifecycleVersion,
  ]);

  useEffect(() => {
    const subscriptions = statusSubscriptions.current;
    return () => {
      subscriptions.forEach((entry) => {
        entry.active = false;
        entry.stop();
      });
      subscriptions.clear();
    };
  }, [authorityEpoch]);

  useEffect(() => {
    if (!userId || !durableStorageAvailable) {
      return undefined;
    }
    void rapidCaptureDb
      .resetForSession(
        userId,
        collectionScoped ? authorityCollectionId : undefined,
      )
      .catch(() => undefined)
      .then(refresh)
      .then(process);
    const unsubscribe = rapidCaptureDb.subscribe(() => {
      void refresh();
    });
    const resume = () => {
      setOnline(navigator.onLine);
      if (document.visibilityState === 'visible' && navigator.onLine)
        void process();
    };
    const markOffline = () => setOnline(false);
    window.addEventListener('online', resume);
    window.addEventListener('offline', markOffline);
    document.addEventListener('visibilitychange', resume);
    const interval = window.setInterval(() => {
      void process();
    }, 5000);
    return () => {
      unsubscribe();
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', markOffline);
      document.removeEventListener('visibilitychange', resume);
      window.clearInterval(interval);
    };
  }, [
    authorityCollectionId,
    collectionScoped,
    durableStorageAvailable,
    process,
    refresh,
    userId,
  ]);

  // Prefetch and enqueue share one authority-scoped request and headroom budget.
  const capabilityRequest = useRef<
    { epoch: string; value: Promise<CapabilityCache> } | undefined
  >(undefined);
  const cachedAiCapability = useCallback(async (): Promise<CapabilityCache> => {
    if (!authority || !authorityEpoch)
      throw new Error('Sign in before capturing items.');
    const cached = capabilityCache.current;
    if (
      cached &&
      cached.epoch === authorityEpoch &&
      cached.expiresAt > Date.now() &&
      cached.remaining > CAPABILITY_LOW_WATER
    )
      return cached;
    if (capabilityRequest.current?.epoch === authorityEpoch)
      return capabilityRequest.current.value;
    const value = getCollectionAiCapability({
      collectionId: authority.collectionId,
      expectedMembershipVersion: authority.membershipVersion,
      workflow: 'image-analysis',
      feature: 'image-analysis',
      imageCount: 1,
    }).then(({ data: capability }) => {
      if (activeAuthorityEpochRef.current !== authorityEpoch)
        throw new Error(
          'Collection access changed. Reopen Add Item before capturing.',
        );
      if (!capability.allowed) {
        capabilityCache.current = undefined;
        throw new Error(
          capability.reason === 'editor-disabled'
            ? 'The collection owner has not enabled editor AI use.'
            : capability.reason === 'insufficient-credits'
              ? 'The collection has insufficient AI credits.'
              : 'Collection AI is temporarily unavailable.',
        );
      }
      const hasHeadroom = typeof capability.headroom === 'number';
      const result = {
        epoch: authorityEpoch,
        costVersion: capability.costVersion,
        expiresAt:
          Date.now() +
          (hasHeadroom ? CAPABILITY_CACHE_MS : CAPABILITY_CACHE_LEGACY_MS),
        // Prefetch does not consume headroom; enqueue spends one per capture.
        remaining: hasHeadroom
          ? capability.headroom!
          : Number.POSITIVE_INFINITY,
      };
      capabilityCache.current = result;
      return result;
    });
    const request = { epoch: authorityEpoch, value };
    capabilityRequest.current = request;
    try {
      return await value;
    } finally {
      if (capabilityRequest.current === request)
        capabilityRequest.current = undefined;
    }
  }, [authority, authorityEpoch]);
  /** Warm the capability result before the first capture of a session. */
  const prefetchAiCapability = useCallback(() => {
    if (
      authority?.compatibilityMode === 'collection-only' &&
      navigator.onLine
    ) {
      void cachedAiCapability().catch(() => undefined);
    }
  }, [authority, cachedAiCapability]);

  const enqueue = useCallback(
    async (draft: RapidCaptureDraft) => {
      if (!authority) throw new Error('Sign in before capturing items.');
      if (!durableStorageAvailable)
        throw new Error(
          'Durable browser storage is unavailable. Use Add Item instead.',
        );
      let acceptedAiCostVersion: string | undefined;
      let aiSkipReason: RapidCaptureRecord['aiSkipReason'];
      let queuedDraft = draft;
      if (
        draft.analyzeImage &&
        authority.compatibilityMode === 'collection-only' &&
        navigator.onLine
      ) {
        const checkStartedAt = performance.now();
        const cached = capabilityCache.current;
        const cacheHit = Boolean(
          cached &&
          cached.epoch === authorityEpoch &&
          cached.expiresAt > Date.now() &&
          cached.remaining > CAPABILITY_LOW_WATER,
        );
        const capability = await cachedAiCapability();
        if (activeAuthorityEpochRef.current !== authorityEpoch)
          throw new Error(
            'Collection access changed. Reopen Add Item before capturing.',
          );
        capability.remaining -= 1;
        acceptedAiCostVersion = capability.costVersion;
        recordRapidCaptureMetric(
          'capability-check',
          performance.now() - checkStartedAt,
          { cached: cacheHit },
        );
      } else if (
        draft.analyzeImage &&
        authority.compatibilityMode === 'collection-only'
      ) {
        queuedDraft = { ...draft, analyzeImage: false };
        aiSkipReason = 'cost-unconfirmed';
      }
      if (activeAuthorityEpochRef.current !== authorityEpoch)
        throw new Error(
          'Collection access changed. Reopen Add Item before capturing.',
        );
      const record = await rapidCaptureDb.enqueue(
        authority,
        queuedDraft,
        acceptedAiCostVersion,
        aiSkipReason,
      );
      trackSessionRecords([record]);
      void process();
      return record;
    },
    [
      authority,
      authorityEpoch,
      cachedAiCapability,
      durableStorageAvailable,
      process,
      trackSessionRecords,
    ],
  );

  const retry = useCallback(
    async (operationId: string) => {
      const record = await rapidCaptureDb.get(operationId);
      if (!record || record.userId !== userId || record.state === 'terminal')
        return;
      if ((record as { schemaVersion: number }).schemaVersion !== 5) {
        if (
          !authority ||
          authority.collectionId !== record.collectionId ||
          authority.membershipVersion !== record.membershipVersion ||
          authority.lifecycleVersion !== record.lifecycleVersion ||
          authority.userId !== record.userId ||
          !online
        )
          return;
        try {
          const existing = await getDocFromServer(
            doc(db, firestorePaths.item(record.collectionId, record.itemId)),
          );
          if (activeAuthorityEpochRef.current !== authorityEpoch) return;
          if (existing.exists()) {
            if (existing.data().captureOperationId !== record.operationId) {
              throw new Error(
                'The saved item does not match this capture. Check it before discarding the local photo.',
              );
            }
            await rapidCaptureDb.remove(record.operationId);
            if (activeAuthorityEpochRef.current !== authorityEpoch) return;
            invalidateInventory({
              userId: record.userId,
              collectionId: record.collectionId,
              resources: ['items'],
              reloadItemIds: [record.itemId],
            });
          } else {
            if (
              record.itemCreated ||
              (record as unknown as { analysisAttempts?: number })
                .analysisAttempts! > 0 ||
              (record as unknown as { ocrAttempts?: number }).ocrAttempts! > 0
            ) {
              throw new Error(
                'This older scan may have a pending AI charge. Check the saved item and reconciliation before restarting. The local photo is retained.',
              );
            }
            // Keep the old target for an uncertain save. A late old commit must
            // conflict with the new operation rather than creating a second item.
            await rapidCaptureDb.enqueue(
              authority,
              {
                image: record.image,
                keywords: record.keywords,
                location: record.location,
                locationId: record.locationId,
                qty: record.qty,
                ...(record.tagIds === undefined
                  ? {}
                  : { tagIds: record.tagIds }),
              },
              undefined,
              record.analyzeImage ? 'cost-unconfirmed' : undefined,
              record.itemId,
            );
            await rapidCaptureDb.remove(record.operationId);
            void process();
          }
          await refresh();
        } catch (error) {
          notify(
            error instanceof Error
              ? error.message
              : 'The saved item could not be checked. The local photo is retained.',
            'error',
          );
        }
        return;
      }
      let acceptedAiCostVersion = record.acceptedAiCostVersion;
      let analysisOperationId = record.analysisOperationId;
      if (
        record.analyzeImage &&
        record.controlAction !== 'save' &&
        record.aiSettlement === 'refunded'
      ) {
        if (
          !authority ||
          authority.collectionId !== record.collectionId ||
          !online
        )
          return;
        const capability = (
          await getCollectionAiCapability({
            collectionId: record.collectionId,
            expectedMembershipVersion: record.membershipVersion,
            workflow: 'image-analysis',
            feature: 'image-analysis',
            imageCount: 1,
          })
        ).data;
        if (
          activeAuthorityEpochRef.current !== authorityEpoch ||
          !capability.allowed
        )
          return;
        analysisOperationId = crypto.randomUUID();
        acceptedAiCostVersion = capability.costVersion;
      }
      if (
        record.analyzeImage &&
        record.controlAction !== 'save' &&
        record.aiSkipReason === 'cost-changed'
      ) {
        if (!authority || authority.collectionId !== record.collectionId)
          return;
        const capability = (
          await getCollectionAiCapability({
            collectionId: record.collectionId,
            expectedMembershipVersion: record.membershipVersion,
            workflow: 'image-analysis',
            feature: 'image-analysis',
            imageCount: 1,
          })
        ).data;
        if (
          activeAuthorityEpochRef.current !== authorityEpoch ||
          !capability.allowed
        )
          return;
        if (record.pendingAiCostVersion !== capability.costVersion) {
          await rapidCaptureDb.put({
            ...record,
            pendingAiCostVersion: capability.costVersion,
            pendingAiCredits: capability.credits,
          });
          await refresh();
          return;
        }
        acceptedAiCostVersion = capability.costVersion;
      }
      await rapidCaptureDb.put({
        ...record,
        state: 'pending',
        controlAction:
          record.controlAction === 'save'
            ? 'save'
            : record.serverAccepted
              ? 'retry'
              : undefined,
        acceptedAiCostVersion,
        analysisOperationId,
        aiSettlement: undefined,
        aiSkipReason: undefined,
        pendingAiCostVersion: undefined,
        pendingAiCredits: undefined,
        nextAttemptAt: undefined,
        lastError: undefined,
      });
      void process();
    },
    [authority, authorityEpoch, notify, online, process, refresh, userId],
  );

  const saveWithoutKeywords = useCallback(
    async (operationId: string) => {
      const record = await rapidCaptureDb.get(operationId);
      if (
        !record ||
        record.userId !== userId ||
        record.schemaVersion !== 5 ||
        record.state === 'terminal'
      )
        return;
      await rapidCaptureDb.put({
        ...record,
        controlAction:
          record.itemCreateAttempted || record.serverAccepted
            ? 'save'
            : undefined,
        ...(record.itemCreateAttempted && !record.serverAccepted
          ? {}
          : { analyzeImage: false }),
        aiSkipReason: undefined,
        pendingAiCostVersion: undefined,
        pendingAiCredits: undefined,
        state: 'pending',
        nextAttemptAt: undefined,
        lastError: undefined,
      });
      void process();
    },
    [process, userId],
  );

  const discard = useCallback(
    async (operationId: string) => {
      const record = await rapidCaptureDb.get(operationId);
      if (!record || record.userId !== userId) return;
      if (record.schemaVersion === 5) rapidCaptureAuthority(record);
      await discardRapidCaptureRecord(record, {
        removeRecord: (id) => rapidCaptureDb.remove(id),
      });
      if (activeAuthorityEpochRef.current !== authorityEpoch) return;
      if (
        record.collectionId === authorityCollectionId &&
        itemCreateMayHaveCommitted(record)
      ) {
        invalidateInventory({
          userId: record.userId,
          collectionId: record.collectionId,
          resources: ['items'],
          reloadItemIds: [record.itemId],
        });
      }
      if (
        sessionBatch.current.userId === userId &&
        sessionBatch.current.operationIds.delete(operationId)
      ) {
        if (!record.itemCreated) {
          sessionBatch.current.itemCount = Math.max(
            0,
            sessionBatch.current.itemCount - 1,
          );
        }
        finishSessionBatchIfComplete();
      }
    },
    [
      authorityCollectionId,
      authorityEpoch,
      finishSessionBatchIfComplete,
      userId,
    ],
  );

  const reconcile = useCallback(async () => {
    if (!userId || !durableStorageAvailable) return;
    const itemsById = new Map(
      authoritativeItems.map((item) => [item.id, item]),
    );
    const queued = (await rapidCaptureDb.list(userId)).filter(
      (record) => record.collectionId === authority?.collectionId,
    );
    if (activeAuthorityEpochRef.current !== authorityEpoch) return;
    trackSessionRecords(queued);
    const confirmed = queued.filter((record) =>
      shouldReconcileRapidCaptureRecord(record, itemsById.get(record.itemId)),
    );
    if (!confirmed.length) return;
    await Promise.all(
      confirmed.map((record) => rapidCaptureDb.remove(record.operationId)),
    );
    if (activeAuthorityEpochRef.current !== authorityEpoch) return;

    if (sessionBatch.current.userId === userId) {
      const reloadItemIds: string[] = [];
      confirmed.forEach((record) => {
        if (!sessionBatch.current.operationIds.delete(record.operationId))
          return;
        reloadItemIds.push(record.itemId);
        if (record.completion === 'analysis-complete') {
          sessionBatch.current.labelCount += 1;
        }
      });
      // A failed or still-pending sibling must not hide an already saved item.
      if (reloadItemIds.length && authorityCollectionId) {
        invalidateInventory({
          userId,
          collectionId: authorityCollectionId,
          resources: ['items'],
          reloadItemIds,
        });
      }
      finishSessionBatchIfComplete();
    }
    await refresh();
  }, [
    authoritativeItems,
    authority?.collectionId,
    authorityCollectionId,
    authorityEpoch,
    durableStorageAvailable,
    finishSessionBatchIfComplete,
    refresh,
    trackSessionRecords,
    userId,
  ]);

  useEffect(() => {
    void reconcile().catch(() => undefined);
  }, [reconcile, records]);

  useEffect(() => {
    if (sessionBatch.current.userId === userId) return;
    sessionBatch.current = newSessionBatch(userId);
    setCompletion(null);
  }, [userId]);

  const clearCompletion = useCallback(() => setCompletion(null), []);

  return {
    available: durableStorageAvailable,
    records,
    stats,
    online,
    completion,
    clearCompletion,
    enqueue,
    prefetchAiCapability,
    retry,
    saveWithoutKeywords,
    discard,
    refresh,
    process,
    reconcile,
  };
}
